import * as Loads from 'react-loads';
import React, { useEffect, useState } from 'react';
import Button from '~/components/ui/Button';
import { Grid } from '~/components/layout/PageStructure';
import Link from '~/components/ui/Link';
import ProductDetails from '../product/ProductDetails';
import PropTypes from 'prop-types';
import client from '~/utils/client';
import { getProductLink } from '~/utils/helpers';
import { productFragment } from '~/utils/queries';
import { useModal } from '~/context/ModalProvider';
import webstoreTracker from '~/helpers/trackers/webstoreTrackerWrapper';

const modalId = 'quickshop';

const fetchData = async productSlug => {
  return await client.request(
    `
    query($slug: String!) {
      product(slug: $slug) {
        ${productFragment}
      }
    }
  `,
    {
      slug: productSlug,
    },
  );
};

const QuickShopRenderer = ({
  productSlug,
  linkedVariantId,
  splitAttr,
  openModal,
  closeModal,
  modalOpen,
}) => {
  const { response: productResponse, isResolved } = Loads.useLoads(
    `quickShop-${linkedVariantId}`,
    fetchData,
    {
      variables: [productSlug],
    },
  );

  useEffect(() => {
    if (isResolved && productResponse?.product && modalOpen) {
      const { product } = productResponse;
      const productLink = getProductLink(
        productSlug,
        linkedVariantId,
        splitAttr,
      );

      openModal({
        id: modalId,
        attrs: {
          onRequestClose: closeModal,
        },
        aria: {
          label: `Product Quickview: ${product.name}`,
        },
        content: (
          <Grid className="my-8 m-0-auto max-w-5/6">
            <ProductDetails
              product={product}
              disableUrlUpdates={true}
              linkedVariantId={linkedVariantId}
              splitAttr={splitAttr}
              isLive={false}
            />
            <Link
              className="block underline mt-7 md:col-start-7 col-span-full"
              to={productLink}
              onClick={closeModal}
            >
              View full details
            </Link>
          </Grid>
        ),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isResolved, productResponse, modalOpen]);

  return null;
};

const QuickShop = ({ productSlug, linkedVariantId, splitAttr }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const { openModal, closeModal } = useModal();

  /**
   * openModal needs to be a separate state from modalStatus because:
   * - We need openModal function to be inside of QuickShopRenderer so it can
   *   use the queried data
   * - If we only use the modals modalStatus prop, we're locked in to having
   *   the QuickShopRenderer always be mounted—which fetches the data
   *   immediately
   */

  const onCloseModal = () => {
    setModalOpen(false);
    closeModal({ id: modalId });
    webstoreTracker.track({
      eventName: 'quickview_closed',
      eventDetails: { productSlug },
    });
  };

  return (
    <>
      <Button
        className="w-full px-1"
        attrs={{
          onClick: () => {
            setModalOpen(true);
            webstoreTracker.track({
              eventName: 'quickview_opened',
              eventDetails: { productSlug },
            });
          },
        }}
        button={{ text: 'Quickview' }}
      />

      {modalOpen && (
        <QuickShopRenderer
          productSlug={productSlug}
          linkedVariantId={linkedVariantId}
          splitAttr={splitAttr}
          openModal={openModal}
          closeModal={onCloseModal}
          modalOpen={modalOpen}
        />
      )}
    </>
  );
};

QuickShop.propTypes = {
  productSlug: PropTypes.string,
  linkedVariantId: PropTypes.string,
};

export default React.memo(QuickShop);
